<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import wootAPI from 'v3/api/apiClient';
import Header from './components/Header.vue';
import Footer from './components/Footer.vue';

const router = useRouter();

const handlePlanAction = plan => {
  if (plan.name === 'Enterprise') {
    router.push('/book-demo');
  } else {
    router.push({
      path: '/app/auth/signup',
      query: {
        plan: plan.name,
        cycle: isYearly.value ? 'yearly' : 'monthly',
      },
    });
  }
};

// Billing state
const isYearly = ref(false);

const toggleBilling = () => {
  isYearly.value = !isYearly.value;
};

// ROI Calculator state
const ticketVolume = ref(2500);
const averageTicketCost = ref(150);

const annualSavings = computed(() => {
  const volume = Number(ticketVolume.value) || 0;
  const cost = Number(averageTicketCost.value) || 0;
  // AI resolves 30% of tickets on average
  return Math.round(volume * 12 * cost * 0.3);
});

// Dynamic Plans & Features
const plans = ref([]);
const comparisonFeatures = ref([]);
const isLoading = ref(true);

const fetchPlans = async () => {
  try {
    const [plansRes, entitlementsRes] = await Promise.all([
      wootAPI.get('/api/v1/subscription_plans'),
      wootAPI.get('/api/v1/subscription_plans/entitlements'),
    ]);
    plans.value = plansRes.data;
    comparisonFeatures.value = entitlementsRes.data;
  } catch (error) {
    console.error('Failed to fetch subscription plans or entitlements:', error);
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  fetchPlans();
});
</script>

<template>
  <div class="scroll-smooth">
    <!-- TopNavBar -->
    <Header />

    <!-- Hero & Pricing Section -->
    <section
      class="py-stack-xl px-margin-mobile md:px-margin-desktop max-w-container-max mx-auto text-center"
    >
      <div class="mb-stack-lg">
        <span
          class="inline-block bg-surface-container-high text-primary px-stack-md py-1 rounded-full text-caption font-semibold mb-stack-sm"
          >PRICING</span>
        <h1
          class="font-display-xl text-display-xl text-deep-navy mb-stack-md leading-tight"
        >
          Scale your support with
          <span class="text-secondary">predictable pricing</span>
        </h1>
        <p
          class="text-body-lg font-body-lg text-on-surface-variant max-w-2xl mx-auto"
        >
          From early-stage startups to global enterprises, OS DESK scales your
          resolution efficiency with AI-native intelligence.
        </p>
      </div>
      <!-- Billing Toggle -->
      <div class="flex justify-center items-center gap-stack-md mb-stack-xl">
        <span id="monthly-label"
class="text-body-md font-semibold text-primary"
          >Monthly</span>
        <button
          id="billing-toggle"
          class="relative w-16 h-8 bg-surface-container-highest rounded-full p-1 transition-all duration-300"
          @click="toggleBilling"
        >
          <div
            id="toggle-circle"
            class="w-6 h-6 bg-deep-navy rounded-full shadow transition-transform"
            :class="isYearly ? 'translate-x-8' : 'translate-x-0'"
          />
        </button>
        <span
          id="yearly-label"
          class="text-body-md font-semibold text-on-surface-variant"
          >Yearly
          <span class="text-secondary text-caption font-bold ml-1">Save 20%</span></span>
      </div>
      <!-- Pricing Grid -->
      <div v-if="isLoading" class="flex justify-center py-20">
        <div
          class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"
        />
      </div>
      <div
        v-else
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-gutter items-stretch"
      >
        <!-- Dynamic Plans rendering -->
        <div
          v-for="plan in plans"
          :key="plan.id"
          class="p-stack-lg rounded-xl flex flex-col text-left transition-all duration-300 relative border border-outline-variant/30"
          :class="[
            plan.name === 'Professional'
              ? 'bg-deep-navy text-white shadow-xl z-10 hover:scale-[1.02]'
              : 'glass-card text-deep-navy hover:scale-[1.02]',
          ]"
        >
          <div
            v-if="plan.name === 'Professional'"
            class="absolute -top-4 left-1/2 -translate-x-1/2 bg-secondary text-on-secondary px-stack-lg py-1 rounded-full text-caption font-bold"
          >
            MOST POPULAR
          </div>
          <div class="mb-stack-lg">
            <h3
              class="font-headline-md text-headline-md mb-stack-xs"
              :class="[
                plan.name === 'Professional' ? 'text-white' : 'text-deep-navy',
              ]"
            >
              {{ plan.name }}
            </h3>
            <p
              class="text-body-md"
              :class="[
                plan.name === 'Professional'
                  ? 'text-surface-variant'
                  : 'text-on-surface-variant',
              ]"
            >
              {{ plan.description }}
            </p>
          </div>
          <div class="mb-stack-lg">
            <div
              v-if="plan.name !== 'Enterprise'"
              class="flex items-baseline gap-1"
            >
              <span
                class="text-headline-lg font-headline-lg"
                :class="[
                  plan.name === 'Professional'
                    ? 'text-white'
                    : 'text-deep-navy',
                ]"
                >₹</span>
              <span
                class="text-display-xl font-display-xl"
                :class="[
                  plan.name === 'Professional'
                    ? 'text-white'
                    : 'text-deep-navy',
                ]"
              >
                {{
                  isYearly
                    ? Math.round(plan.price_in_rupees * 0.8)
                    : plan.price_in_rupees
                }}
              </span>
              <span
                class="font-body-md"
                :class="[
                  plan.name === 'Professional'
                    ? 'text-surface-variant'
                    : 'text-on-surface-variant',
                ]"
                >/mo</span>
            </div>
            <div v-else class="flex items-baseline gap-1">
              <span
                class="text-display-xl font-display-xl"
                :class="[
                  plan.name === 'Professional'
                    ? 'text-white font-bold'
                    : 'text-deep-navy font-bold',
                ]"
                >Custom</span>
            </div>
            <p
              class="text-caption mt-2"
              :class="[
                plan.name === 'Professional'
                  ? 'text-surface-variant'
                  : 'text-on-surface-variant',
              ]"
            >
              {{
                plan.name === 'Enterprise'
                  ? 'Contact us for custom terms.'
                  : isYearly
                    ? `Billed annually (₹${(Math.round(plan.price_in_rupees * 0.8) * 12).toLocaleString('en-IN')}/yr).`
                    : 'Billed monthly.'
              }}
            </p>
          </div>
          <ul class="space-y-stack-md mb-stack-xl flex-grow">
            <li class="flex items-center gap-stack-sm text-body-md">
              <span
                class="material-symbols-outlined text-secondary"
                style="font-variation-settings: 'FILL' 1"
                >check_circle</span>
              <span>{{
                  plan.agent_limit === -1 ? 'Unlimited' : plan.agent_limit
                }}
                Agents included</span>
            </li>
            <li class="flex items-center gap-stack-sm text-body-md">
              <span
                class="material-symbols-outlined text-secondary"
                style="font-variation-settings: 'FILL' 1"
                >check_circle</span>
              <span>{{
                  plan.whatsapp_inbox_limit === -1
                    ? 'Unlimited'
                    : plan.whatsapp_inbox_limit
                }}
                WhatsApp Numbers</span>
            </li>
            <li class="flex items-center gap-stack-sm text-body-md">
              <span
                class="material-symbols-outlined text-secondary"
                style="font-variation-settings: 'FILL' 1"
                >check_circle</span>
              <span>{{
                  plan.ai_chat_credits === -1
                    ? 'Unlimited'
                    : plan.ai_chat_credits
                }}
                AI Replies / mo</span>
            </li>
            <li class="flex items-center gap-stack-sm text-body-md">
              <span
                class="material-symbols-outlined text-secondary"
                style="font-variation-settings: 'FILL' 1"
                >check_circle</span>
              <span>{{
                  plan.voice_minutes === -1
                    ? 'Custom/Unlimited'
                    : plan.voice_minutes
                }}
                Voice minutes</span>
            </li>
            <li
              v-for="feat in plan.features"
              :key="feat"
              class="flex items-center gap-stack-sm text-body-md"
            >
              <span
                class="material-symbols-outlined text-secondary"
                style="font-variation-settings: 'FILL' 1"
                >check_circle</span>
              <span>{{ feat }}</span>
            </li>
          </ul>
          <button
            class="w-full font-bold py-stack-md rounded-lg transition-all"
            :class="[
              plan.name === 'Professional'
                ? 'bg-secondary text-white hover:opacity-90 shadow-lg'
                : 'border-2 border-deep-navy text-deep-navy hover:bg-deep-navy hover:text-white',
            ]"
            @click="handlePlanAction(plan)"
          >
            {{
              plan.name === 'Enterprise' ? 'Contact Sales' : 'Start Free Trial'
            }}
          </button>
        </div>
      </div>
    </section>
    <!-- ROI Calculator Section -->
    <section class="bg-ai-glow py-stack-xl border-y border-outline-variant/30">
      <div class="max-w-4xl mx-auto px-margin-mobile text-center">
        <h2
          class="font-headline-lg text-headline-lg text-deep-navy mb-stack-md"
        >
          Calculate your AI ROI
        </h2>
        <div
          class="glass-card p-stack-xl rounded-2xl grid grid-cols-1 md:grid-cols-2 gap-stack-xl items-center text-left"
        >
          <div class="space-y-stack-lg">
            <div>
              <label
                class="block text-label-md font-label-md text-on-surface-variant mb-2"
                >Monthly Ticket Volume</label>
              <input
                id="roi-volume"
                v-model="ticketVolume"
                class="w-full accent-secondary"
                max="10000"
                min="100"
                step="100"
                type="range"
              />
              <div
                class="flex justify-between text-caption mt-1 font-semibold text-deep-navy"
              >
                <span>100</span>
                <span id="volume-val">{{
                  Number(ticketVolume).toLocaleString()
                }}</span>
                <span>10,000</span>
              </div>
            </div>
            <div>
              <label
                class="block text-label-md font-label-md text-on-surface-variant mb-2"
                >Average Ticket Cost (₹)</label>
              <input
                id="roi-cost"
                v-model="averageTicketCost"
                class="w-full p-stack-md border-border-subtle rounded-lg font-body-md outline-none ai-border-glow"
                type="number"
              />
            </div>
          </div>
          <div
            class="bg-deep-navy p-stack-lg rounded-xl text-center min-w-0 flex flex-col justify-center"
          >
            <p class="text-surface-variant text-label-md mb-2">
              Estimated Annual Savings
            </p>
            <h4
              id="roi-result"
              class="text-3xl sm:text-4xl md:text-2xl lg:text-3xl xl:text-4xl font-bold text-secondary-fixed break-words leading-tight px-1"
            >
              ₹{{ annualSavings.toLocaleString() }}
            </h4>
            <p class="text-caption text-surface-variant/70 mt-4 italic">
              *Based on average 30% AI resolution rate improvement
            </p>
          </div>
        </div>
      </div>
    </section>
    <!-- Detailed Feature Comparison -->
    <section
      class="py-stack-xl px-margin-mobile md:px-margin-desktop max-w-container-max mx-auto overflow-x-auto hide-scrollbar"
    >
      <h2
        class="font-headline-lg text-headline-lg text-deep-navy mb-stack-xl text-center"
      >
        Compare features
      </h2>
      <table class="w-full min-w-[800px] border-collapse">
        <thead>
          <tr class="border-b border-outline-variant">
            <th
              class="p-stack-md text-left text-label-md font-label-md text-on-surface-variant"
            >
              FEATURE
            </th>
            <th
              v-for="plan in plans"
              :key="plan.id"
              class="p-stack-md text-center text-label-md font-label-md text-deep-navy"
            >
              {{ plan.name.toUpperCase() }}
            </th>
          </tr>
        </thead>
        <tbody class="text-body-md">
          <tr
            v-for="f in comparisonFeatures"
            :key="f.key"
            class="border-b border-outline-variant/30 hover:bg-surface-container transition-colors"
          >
            <td class="p-stack-md font-semibold text-deep-navy">
              {{ f.name }}
            </td>
            <td
              v-for="plan in plans"
              :key="plan.id"
              class="p-stack-md text-center"
            >
              <span
                v-if="plan.limits[f.key] === 'true'"
                class="material-symbols-outlined text-secondary"
                style="font-variation-settings: 'FILL' 1"
                >check</span>
              <span
                v-else-if="
                  plan.limits[f.key] === 'false' || !plan.limits[f.key]
                "
                class="text-on-surface-variant/40"
                >—</span>
              <span v-else class="text-deep-navy font-semibold">
                {{
                  plan.limits[f.key] === '-1' ? 'Unlimited' : plan.limits[f.key]
                }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </section>
    <!-- FAQ Section -->
    <section class="py-stack-xl bg-white">
      <div class="max-w-3xl mx-auto px-margin-mobile">
        <h2
          class="font-headline-lg text-headline-lg text-deep-navy mb-stack-xl text-center"
        >
          Frequently asked questions
        </h2>
        <div class="space-y-stack-md">
          <!-- FAQ Item 1 -->
          <details class="group glass-card rounded-xl">
            <summary
              class="list-none p-stack-lg cursor-pointer flex justify-between items-center font-semibold text-deep-navy"
            >
              How does the AI resolution rate affect billing?
              <span
                class="material-symbols-outlined group-open:rotate-180 transition-transform"
                >expand_more</span>
            </summary>
            <div class="px-stack-lg pb-stack-lg text-on-surface-variant">
              Our pricing is agent-based, not ticket-based. AI resolutions are
              included in your subscription, meaning the more you automate, the
              higher your ROI without increasing your OS DESK costs.
            </div>
          </details>
          <!-- FAQ Item 2 -->
          <details class="group glass-card rounded-xl">
            <summary
              class="list-none p-stack-lg cursor-pointer flex justify-between items-center font-semibold text-deep-navy"
            >
              Can we switch plans at any time?
              <span
                class="material-symbols-outlined group-open:rotate-180 transition-transform"
                >expand_more</span>
            </summary>
            <div class="px-stack-lg pb-stack-lg text-on-surface-variant">
              Yes, you can upgrade or downgrade your plan directly from your
              dashboard. Plan changes are prorated to the next billing cycle.
            </div>
          </details>
          <!-- FAQ Item 3 -->
          <details class="group glass-card rounded-xl">
            <summary
              class="list-none p-stack-lg cursor-pointer flex justify-between items-center font-semibold text-deep-navy"
            >
              Do you offer discounts for non-profits?
              <span
                class="material-symbols-outlined group-open:rotate-180 transition-transform"
                >expand_more</span>
            </summary>
            <div class="px-stack-lg pb-stack-lg text-on-surface-variant">
              Absolutely! We offer a 50% discount for registered non-profit
              organizations. Contact our sales team to verify your status.
            </div>
          </details>
        </div>
      </div>
    </section>
    <!-- Final CTA Section -->
    <section
      class="py-stack-xl bg-deep-navy text-white text-center relative overflow-hidden"
    >
      <div class="relative z-10 max-w-2xl mx-auto px-margin-mobile">
        <h2 class="font-headline-lg text-headline-lg mb-stack-md">
          Ready to resolve faster?
        </h2>
        <p class="text-body-lg text-surface-variant mb-stack-lg">
          Join 2,000+ support teams using OS DESK to drive efficiency and
          customer satisfaction.
        </p>
        <div class="flex flex-col sm:flex-row justify-center gap-stack-md">
          <button
            class="bg-secondary text-white font-bold px-stack-xl py-stack-md rounded-lg shadow-lg hover:opacity-90 transition-all"
          >
            Start 14-day Free Trial
          </button>
          <button
            class="border border-surface-variant text-white font-bold px-stack-xl py-stack-md rounded-lg hover:bg-white/10 transition-all"
          >
            Request a Demo
          </button>
        </div>
        <p class="text-caption text-surface-variant/60 mt-stack-md">
          No credit card required • Cancel anytime
        </p>
      </div>
    </section>

    <!-- Footer -->
    <Footer />
  </div>
</template>
